import { RedoButton } from "@redotech/arbiter/buttons/redo-button";
import { Card } from "@redotech/arbiter/card";
import { Flex } from "@redotech/arbiter/flex";
import { RedoIcon } from "@redotech/arbiter/icon/redo-icon";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/arbiter/input/redo-textarea-input-field";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import CheckIcon from "@redotech/redo-icons/check.svg";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import XIcon from "@redotech/redo-web/icon-old/x.svg";
import { memo, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";

export const NetsuiteIntegrationCard = memo(function NetsuiteIntegrationCard() {
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  // State for the item fulfillment ID input
  const [itemFulfillmentId, setItemFulfillmentId] = useState("");
  // State for copy button
  const [copiedItemFulfillment, setCopiedItemFulfillment] = useState(false);
  const [copiedError, setCopiedError] = useState(false);

  // Fetch the Netsuite integration settings
  const settingsLoad = useLoad(async () => {
    return await rpcClient.getNetsuiteIntegrationSettings({ teamId });
  }, [teamId, rpcClient]);

  // Set up the sync item fulfillment load trigger
  const [syncItemFulfillmentLoad, syncItemFulfillment] = useTriggerLoad(
    async () => {
      if (!itemFulfillmentId.trim()) {
        return null;
      }

      return await rpcClient.syncNetsuiteItemFulfillment({
        teamId,
        itemFulfillmentId: itemFulfillmentId.trim(),
      });
    },
  );

  // Handle the sync button click
  const handleSync = useHandler(() => {
    syncItemFulfillment();
  });

  // Handle copy to clipboard
  const handleItemFulfillmentCopy = useHandler(async () => {
    if (formattedItemFulfillment) {
      await navigator.clipboard.writeText(formattedItemFulfillment);
      setCopiedItemFulfillment(true);
    }
  });

  const handleErrorCopy = useHandler(async () => {
    if (error) {
      await navigator.clipboard.writeText(error);
      setCopiedError(true);
    }
  });

  // Format the item fulfillment data as JSON
  const formattedItemFulfillment = useMemo(() => {
    if (!syncItemFulfillmentLoad.value?.itemFulfillment) {
      return null;
    }

    return JSON.stringify(
      syncItemFulfillmentLoad.value.itemFulfillment,
      null,
      2,
    );
  }, [syncItemFulfillmentLoad.value]);

  // If the integration doesn't exist, don't render the card
  if (settingsLoad.value?.settings === null) {
    return null;
  }

  const error =
    syncItemFulfillmentLoad.error || syncItemFulfillmentLoad.value?.error;

  return (
    <Card title="Sync Netsuite item fulfillment">
      <Flex dir="column" gap="md">
        <Flex align="flex-end" dir="row" gap="md">
          <RedoTextInput
            placeholder="Enter NetSuite Item Fulfillment ID"
            setValue={setItemFulfillmentId}
            value={itemFulfillmentId}
          />
          <RedoButton
            disabled={
              !itemFulfillmentId.trim() || syncItemFulfillmentLoad.pending
            }
            hierarchy="primary"
            onClick={handleSync}
            pending={syncItemFulfillmentLoad.pending}
            text="Sync"
          />
        </Flex>

        {error || formattedItemFulfillment ? (
          <Text fontSize="sm" fontWeight="regular">
            Results:
          </Text>
        ) : undefined}

        <Flex dir="column" gap="xl">
          <Flex dir="column" gap="sm">
            {syncItemFulfillmentLoad.value && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Got Json from Netsuite? -
                </Text>
                {formattedItemFulfillment ? (
                  <RedoIcon color="success" Icon={CheckIcon} />
                ) : (
                  <RedoIcon color="error" Icon={XIcon} />
                )}
              </Flex>
            )}

            {syncItemFulfillmentLoad.value && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Item Fulfillment passed zod parsing? -
                </Text>
                {syncItemFulfillmentLoad.value
                  ?.successfullyParsedItemFulfillment ? (
                  <RedoIcon color="success" Icon={CheckIcon} />
                ) : (
                  <RedoIcon color="error" Icon={XIcon} />
                )}
              </Flex>
            )}

            {syncItemFulfillmentLoad.value && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Parsed Redo Location from Netsuite data? -
                </Text>
                {syncItemFulfillmentLoad.value?.successfullyParsedLocation ? (
                  <RedoIcon color="success" Icon={CheckIcon} />
                ) : (
                  <RedoIcon color="error" Icon={XIcon} />
                )}
              </Flex>
            )}

            {syncItemFulfillmentLoad.value && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Parsed Redo Inventory Items from Netsuite data? -
                </Text>
                {syncItemFulfillmentLoad.value
                  ?.successfullyParsedInventoryItems ? (
                  <RedoIcon color="success" Icon={CheckIcon} />
                ) : (
                  <RedoIcon color="error" Icon={XIcon} />
                )}
              </Flex>
            )}

            {syncItemFulfillmentLoad.value && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Parsed Redo Fulfillment Order from Netsuite data? -
                </Text>
                {syncItemFulfillmentLoad.value
                  ?.successfullyParsedFulfillmentOrder ? (
                  <RedoIcon color="success" Icon={CheckIcon} />
                ) : (
                  <RedoIcon color="error" Icon={XIcon} />
                )}
              </Flex>
            )}

            {syncItemFulfillmentLoad.value && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Parsed Redo Fulfillments from Netsuite data? -
                </Text>
                {syncItemFulfillmentLoad.value
                  ?.successfullyParsedRedoFulfillment ? (
                  <RedoIcon color="success" Icon={CheckIcon} />
                ) : (
                  <RedoIcon color="error" Icon={XIcon} />
                )}
              </Flex>
            )}

            {syncItemFulfillmentLoad.value && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Created new Fulfillment Group(s)? -
                </Text>
                {(syncItemFulfillmentLoad.value?.newFulfillmentGroupIds
                  ?.length || 0) > 0 ? (
                  <RedoIcon color="success" Icon={CheckIcon} />
                ) : (
                  <RedoIcon color="error" Icon={XIcon} />
                )}
              </Flex>
            )}

            {(syncItemFulfillmentLoad.value?.newFulfillmentGroupIds?.length ||
              0) > 0 && (
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Fulfillment Group IDs:
                </Text>
                <Text fontSize="xs" fontWeight="regular">
                  {syncItemFulfillmentLoad.value?.newFulfillmentGroupIds?.join(
                    ", ",
                  ) || ""}
                </Text>
              </Flex>
            )}
          </Flex>

          {error && (
            <Flex dir="column" gap="sm">
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Error:
                </Text>
                <RedoButton
                  hierarchy="secondary"
                  IconLeading={copiedError ? CheckIcon : CopyIcon}
                  onClick={handleErrorCopy}
                  size="xs"
                  text={copiedError ? "Copied" : "Copy"}
                />
              </Flex>
              <RedoTextareaInputField
                disabled
                setValue={() => {}} // noop
                value={error}
              />
            </Flex>
          )}

          {formattedItemFulfillment && (
            <Flex dir="column" gap="sm">
              <Flex align="center" dir="row" gap="sm">
                <Text fontSize="xs" fontWeight="regular">
                  Item Fulfillment Json:
                </Text>
                <RedoButton
                  hierarchy="secondary"
                  IconLeading={copiedItemFulfillment ? CheckIcon : CopyIcon}
                  onClick={handleItemFulfillmentCopy}
                  size="xs"
                  text={copiedItemFulfillment ? "Copied" : "Copy"}
                />
              </Flex>
              <RedoTextareaInputField
                disabled
                setValue={() => {}} // noop
                value={formattedItemFulfillment}
              />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Card>
  );
});
